<template>
  <div class="que-container" @click="GotoResultView">
    <div class ="que-title-container">
      <div class="quetionHead">第<span class="number">{{ currentQuestionId }}</span>题，总共<span class="number">{{ totalQuestions }}</span>题</div>
      <div class="quetionTitle">{{ currentQuestion.question }}</div>
    </div>

    <div class="question-content">
      <div class="options-container">
        <div class="options-row" v-for="(row, rowIndex) in optionRows" :key="rowIndex">
          <div 
            v-for="(option, index) in row" 
            :key="option.id"
            class="option-item"
            :class="[
              `option-${rowIndex * 1 + index + 1}`,
              { 'selected': option.isSelected }
            ]"
            @click.stop="handleOptionClick(rowIndex * 1 + index)"
          >
            <span class="option-text">{{ option.text }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="next-button" @click="gotoNextQuestion">确定答案</div>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router'
import { ref, computed, onMounted } from 'vue'
import questionsData from '../assets/question.json'

// 创建一个简单的 Toast 函数
const showToast = (message, duration = 1500) => {
  // 创建 toast 元素
  let toastEl = document.querySelector('.toast-message')
  if (toastEl) {
    toastEl.remove()
  }
  
  toastEl = document.createElement('div')
  toastEl.className = 'toast-message'
  // 将换行符转换为 <br> 标签以支持换行
  toastEl.innerHTML = message.replace(/\n/g, '<br>')
  toastEl.style.cssText = `
    position: fixed;
    bottom: 20%;
    left: 50%;
    transform: translateX(-50%);
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 12px 24px;
    border-radius: 24px;
    font-size: 16px;
    z-index: 10000;
    min-width: 200px;
    text-align: center;
    line-height: 1.5;
  `
  
  document.body.appendChild(toastEl)
  
  // 设置定时器移除 toast
  setTimeout(() => {
    if (document.contains(toastEl)) {
      document.body.removeChild(toastEl)
    }
  }, duration)
}

const router = useRouter()

// 从 question.json 加载题目数据
const questions = ref(questionsData.questions)
const currentQuestionIndex = ref(0) // 当前题目索引

// 获取当前题目
const currentQuestion = computed(() => {
  return questions.value[currentQuestionIndex.value] || {}
})

// 获取当前题目ID
const currentQuestionId = computed(() => {
  return currentQuestion.value.id || currentQuestionIndex.value + 1
})

// 总题目数
const totalQuestions = computed(() => {
  return questions.value.length
})

// 定义选项数据，基于当前题目
const options = ref([])

// 更新选项数据
const updateOptions = () => {
  if (currentQuestion.value && currentQuestion.value.options) {
    options.value = Object.entries(currentQuestion.value.options).map(([key, value]) => ({
      id: key,
      text: value,
      isSelected: false
    }))
  }
}

// 将选项按每行两个进行分组
const optionRows = computed(() => {
  const rows = []
  for (let i = 0; i < options.value.length; i += 1) {
    rows.push(options.value.slice(i, i + 1))
  }
  return rows
})

// 处理选项点击事件
const handleOptionClick = (index) => {
  // 切换选项的选择状态（支持多选）
  options.value[index].isSelected = !options.value[index].isSelected
}

// 存储用户得分
const score = ref(0)

// 跳转到下一题
const gotoNextQuestion = () => {
  // 获取用户选择的选项
  const userSelectedOptions = options.value.filter(option => option.isSelected)
  
  // 检查用户是否选择了选项
  if (userSelectedOptions.length === 0) {
    showToast('请至少选择一个选项作答')
    return
  }
  
  // 获取当前题目的正确答案
  const correctOptions = currentQuestion.value.correct_options || {}
  
  // 检查用户答案是否正确
  let isCorrect = true
  
  // 检查用户选择的选项是否都在正确答案中
  for (const option of userSelectedOptions) {
    if (!correctOptions[option.id]) {
      isCorrect = false
      break
    }
  }
  
  // 检查正确答案是否都被用户选择了
  const correctOptionIds = Object.keys(correctOptions)
  for (const id of correctOptionIds) {
    const userSelected = userSelectedOptions.find(opt => opt.id === id)
    if (!userSelected) {
      isCorrect = false
      break
    }
  }
  
  if (isCorrect) {
    // 答题正确，加分
    score.value += 10
    showToast(`回答正确！当前得分：${score.value}`)
  } else {
    // 答题错误，显示正确答案和描述
    const correctDescriptions = currentQuestion.value.correct_descriptions || '无详细解释'
    const correctOptionTexts = correctOptionIds.join('、')
    
    showToast(`回答错误！\n正确答案：${correctOptionTexts}\n解析：${correctDescriptions}`)
  }
  
  // 无论是否正确，都进入下一题
  if (currentQuestionIndex.value < questions.value.length - 1) {
    currentQuestionIndex.value++ // ID加1
    updateOptions() // 重新读取question.json的数据并显示下一题的题目及选项
  } else {
    // 所有题目完成，延迟2秒后跳转并统计分数
    setTimeout(() => {
      router.push({
        path: '/result',
        query: { score: score.value } // 传递最终得分
      })
    }, 1000)
  }
}

// 组件挂载时初始化选项
onMounted(() => {
  updateOptions()
})
</script>

<style lang="scss" scoped>
.que-container {
  /* 设置背景图 */
  background-image: url('../assets/quebg.png');
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 120px;
  box-sizing: border-box;
}

.que-title-container {
  width: 603px;
  background-image: url('../assets/que-title.png');
  background-size: 100% 100%; /* 使用 100% 100% 确保背景随容器拉伸 */
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  align-items: flex-start; /* 改为 flex-start，方便子元素对齐 */
  justify-content: center;
  padding: 20px 40px; /* 增加左右内边距，确保文字不紧贴边缘 */
  box-sizing: border-box;
}

.quetionHead {
  width: 100%;
	font-family: FZLTHK--GBK1-0;
	font-size: 24px;
	font-weight: bold;
	font-stretch: normal;
	line-height: 48px;
	letter-spacing: 0px;
	color: #5f2600;
  margin-bottom: 10px;
  text-align: center;
}

.number {
  color: #f23c13;
}

.question-content {
  display: flex;
  flex-direction: column;
  align-items: center; /* 水平居中 */
  width: 100%;
}

.quetionTitle {
  width: 100%; /* 确保占据全宽 */
	font-family: FZLTHK--GBK1-0;
	font-size: 30px;
	font-weight: normal;
	font-stretch: normal;
	line-height: 42px;
	letter-spacing: 0px;
	color: #5f2600;
  text-align: left; /* 强制文字居左 */
}

.options-container {
  width: 90%;
  display: flex;
  flex-direction: column;
  gap: 0px; /* 行与行之间的间距 */
}

.options-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-top: 20px;
  gap: 5px; /* 同一行两个选项之间的间距 */
}

.option-item {
  width: calc(100% - 10px); /* 每行两个选项，减去间距的一半 */
  height: 94px;
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: flex-start; /* 文字居左显示 */
  padding-left: 120px; /* 为背景图中的 A,B,C,D 留出空间 */
  cursor: pointer;
  box-sizing: border-box;
}

/* 动态背景图配置 */
.option-1 { background-image: url('../assets/option-1.png'); }
.option-2 { background-image: url('../assets/option-2.png'); }
.option-3 { background-image: url('../assets/option-3.png'); }
.option-4 { background-image: url('../assets/option-4.png'); }
.option-5 { background-image: url('../assets/option-5.png'); }

/* 选中态背景图配置 */
.option-1.selected { background-image: url('../assets/option-select-1.png'); }
.option-2.selected { background-image: url('../assets/option-select-2.png'); }
.option-3.selected { background-image: url('../assets/option-select-3.png'); }
.option-4.selected { background-image: url('../assets/option-select-4.png'); }
.option-5.selected { background-image: url('../assets/option-select-5.png'); }

.option-item:hover {
  opacity: 0.9;
}

.option-text {
	font-family: FZLTHK--GBK1-0;
	font-size: 28px;
	font-weight: normal;
	font-stretch: normal;
	line-height: 48px;
	letter-spacing: 0px;
	color: #333333;
  text-align: center;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}

.option-item.selected .option-text {
  color:  #333333;; /* 选中时文字改为白色以匹配深色背景 */
}

.next-button {
	width: 317px;
	height: 68px;
	background-image: linear-gradient(0deg, 
		#f4cd2d 0%, 
		#ffeaad 100%);
	border-radius: 34px;
  margin-top: 60px;
  margin-bottom: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
	font-family: MicrosoftYaHei-Bold;
	font-size: 34px;
	font-weight: bold;
	line-height: 36px;
	letter-spacing: 3px;
	color: #d7461e;
}
</style>